/**
 * School Icon Library Entity Schemas
 *
 * A per-school library of sanitized, single-color SVG icons a school can pick
 * on any menu surface, alongside the built-in icon set (Custom SVG Menu Icons
 * plan, U1). Underlying Prisma model: `SchoolIconLibraryEntry` (apps/backend).
 *
 * `markup` is the delivered artifact itself (KTD18) — normalized, sanitized,
 * single-color SVG text — never a URL, key, or bucket pointer. There is no
 * object store and no retained original; a later sanitizer advisory is
 * answered by re-running the sanitizer over this stored markup.
 *
 * A menu item does NOT carry this entry directly. It references one by a
 * namespaced value inside the icon field it already has (KTD1) — see
 * `parseIconReference`/`formatCustomIconReference` below. A sibling field
 * would be silently stripped by the tile screen's unknown-key discard
 * (`page.ts` — `TileScreenContentSchema`/`ContentPageSchema` `.strip()`), so
 * the reference rides inside the same string the built-in icon name does.
 * This is the ONE place that grammar is decided; backend, web, and mobile all
 * import it rather than re-implementing the prefix and separator.
 */

import { z } from 'zod'

export const SchoolIconLibraryEntrySchema = z.object({
  /** SchoolIconLibraryEntry publicId. */
  id: z.string(),
  name: z.string().min(1),
  /** Normalized, sanitized, single-color SVG markup - the delivered bytes. */
  markup: z.string(),
  /** Sanitizer/normalize-pipeline version that produced `markup` (KTD15). */
  sanitizerVersion: z.string(),
  processedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type SchoolIconLibraryEntry = z.infer<typeof SchoolIconLibraryEntrySchema>

// ============================================
// Namespaced custom-icon reference (KTD1)
// ============================================

/**
 * Prefix that marks an icon field value as a custom-library reference rather
 * than a built-in icon name. Built-in names are plain identifiers (e.g.
 * `home`, `calendar-outline`) with no colon, so anchoring on a colon-suffixed
 * prefix at the START of the string cannot collide with one. Anchoring at the
 * start (not merely containing the prefix) is what keeps a built-in name that
 * happens to embed this text mid-string from being mistaken for a reference.
 */
export const CUSTOM_ICON_REFERENCE_PREFIX = 'custom-icon:' as const

export interface CustomIconReference {
  readonly isCustom: true
  /** The referenced SchoolIconLibraryEntry's publicId. */
  readonly entryPublicId: string
}

export interface BuiltInIconReference {
  readonly isCustom: false
}

export type ParsedIconReference = CustomIconReference | BuiltInIconReference

/**
 * The single parse helper for the namespaced reference grammar (KTD1).
 * Answers "is this a custom reference, and which entry does it name" for an
 * icon field's raw string value. Reused verbatim by backend (delete-refusal
 * scan, config delivery), web (picker read/write), and mobile (the resolver).
 *
 * Takes `unknown` rather than `string` because the icon field it reads from
 * is unvalidated legacy config in some callers (e.g. content authored before
 * this feature existed) — never throws, and a malformed value (wrong prefix,
 * not even a string, or the prefix with nothing after it) parses as
 * `{ isCustom: false }` so callers can fall through to built-in lookup and
 * its existing missing-icon fallback (R14) without a try/catch at every call
 * site.
 */
export function parseIconReference(value: unknown): ParsedIconReference {
  if (typeof value !== 'string' || !value.startsWith(CUSTOM_ICON_REFERENCE_PREFIX)) {
    return { isCustom: false }
  }
  const entryPublicId = value.slice(CUSTOM_ICON_REFERENCE_PREFIX.length)
  if (!entryPublicId) {
    return { isCustom: false }
  }
  return { isCustom: true, entryPublicId }
}

/**
 * Builds the namespaced reference value to write into an icon field. The
 * inverse of `parseIconReference`'s custom branch. Kept alongside the parser
 * so the prefix and separator are decided in exactly one place.
 *
 * Throws on an empty `entryPublicId` rather than silently producing a value
 * that `parseIconReference` would turn back around and treat as not-custom —
 * a caller passing an empty id is a bug at the call site, not a value worth
 * degrading quietly.
 */
export function formatCustomIconReference(entryPublicId: string): string {
  if (!entryPublicId) {
    throw new Error('formatCustomIconReference requires a non-empty entryPublicId')
  }
  return `${CUSTOM_ICON_REFERENCE_PREFIX}${entryPublicId}`
}
